import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * Reads body_measurement rows for the user, newest-first, optionally
 * limited to the last N days for chart use.
 */

export type BodyMeasurementRow = {
  id: string;
  ts: string;
  weight_kg: number | null;
  body_fat_pct: number | null;
  muscle_mass_kg: number | null;
  sensor_source: string;
};

export function useBodyMeasurements(
  options?: { lookbackDays?: number; limit?: number },
) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const lookbackDays = options?.lookbackDays;
  const limit = options?.limit ?? 365;
  return useQuery({
    queryKey: ["body_measurements", userId, lookbackDays, limit],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      let query = supabase
        .from("body_measurement")
        .select("id, ts, weight_kg, body_fat_pct, muscle_mass_kg, sensor_source")
        .eq("user_id", userId)
        .order("ts", { ascending: false })
        .limit(limit);
      if (lookbackDays) {
        const since = new Date();
        since.setDate(since.getDate() - lookbackDays);
        query = query.gte("ts", since.toISOString());
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as BodyMeasurementRow[];
    },
  });
}
